import React, { useEffect, useRef, useContext, useState, useMemo, useCallback } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PreviewModeBanner from '../../../components/PreviewModeBanner';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import PADSideNavbar from '../9_postAssessmentDiscussion/components/PADSideNavbar';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { PlusIcon, CheckIcon } from '@heroicons/react/solid';
import { CheckCircleIcon } from '@heroicons/react/solid';
// import { DocumentTextIcon, CheckCircleIcon, MicrophoneIcon, StopIcon, QuestionMarkCircleIcon } from '@heroicons/react/solid';
// The adult DocumentPreviewPanel reads adult document slots that a CYP child
// never has, so it rendered nothing here. The CYP panel reads the CYP slots
// (documents.cypAdhdCiReport / cypAdhdSsiReport) and approval flags.
import CypAdhdSupportingDocumentsPanel from '../../../components/CypAdhdSupportingDocumentsPanel';
import { auth } from '../../../firebase';
import ModuleCompletionDialog, { CancelButton, GenerateReportButton, SummarySection } from '../../../components/ModuleCompletionDialog';
import StructuredNotesEditor from '../12_observation/components/StructuredNotesEditor';
import { normaliseNotesValue } from '../../../utils/autismReviewData';
import { hasNoteText, sameNotes, resolveCypPadGoalsNotes } from './padNotesHelpers';
import { serverTimestamp } from 'firebase/firestore';
import { buildFirstGeneratedFields, buildFirstCompletedFields, buildPadCompletionFields, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { ShouldDisplayElement } from '../../../utils/ShouldDisplayElement';
import InfoTooltip from '../../../components/InfoTooltip';
import ModularStepFooter from '../../../components/autismModular/ModularStepFooter';
import { SearchIcon, ClockIcon, XIcon } from '@heroicons/react/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { formatDateTime } from '../../../utils/timestampUtils';
import { FormattedAnswer, normalizeRawAnswer, buildDisplayAnswer } from '../../../utils/formDisplayUtils';
import { isPadAdhdOutcomePositive } from '../../../utils/conditionChecks';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import PatientContractTypeBadge from '../../../components/PatientContractTypeBadge';
import {
  getPadStageOrder,
  getPadSidebarIndex,
  getPadSidebarSections,
  getPadNavLinks,
  rewriteSubsectionTitleDisplay,
  readIsCarryOutHealthChecksNow,
  buildPadSidebarUnlockFlags,
} from './cypPadNavigationUtils';
import {
  findCiAssessmentGoalsQuestion,
  resolveAssessmentGoalsDisplay,
  ASSESSMENT_GOALS_SERVICE_USER_VAR,
} from '../../../utils/mdtPlanUtils';
import PadRedoArchiveBanner from '../../../components/PadRedoArchiveBanner';

// The CYP PAD's "Review of Assessment" section now has an LLM pipeline
// (llm_processor_cyp_pad.py) that drafts a summary-of-findings paragraph
// and a goals paragraph. With the pipeline enabled, section completion
// triggers draft generation and the clinician reviews/approves via the
// PAD review page (RD_PAD_CypAdhd), writing padReportApproved on exit.
// Treatment Options, Health Checks, and Agreed Medication Plan remain
// deterministic and need no generation or review step.
const CYP_PAD_LLM_PIPELINE_ENABLED = true;

export default function PADReviewDiagnosis() {
  const { patientId } = useParams();
  // Preview mode (?preview=true from the overview's Preview button): the
  // preview-aware navigate keeps the flag on every PAD step hop (sidebar,
  // Next, Back) and the flag forces canEdit=false below even for the
  // assigned PAD clinician — the same wiring as the Adult Autism PAD wizard.
  const navigate = usePreviewAwareNavigate();
  const isPreviewMode = useIsPreviewMode();
  const { patientDataModel, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  // The modular context carries the CYP document metadata and per-stage
  // approval flags that the Supporting Documents panel needs. The bridge's
  // PatientDataContext provides patientDataModel.documents (from the header)
  // but not the approval flags, which live on reportContent.
  const { header: modularHeader, reportContent: modularReportContent } = useContext(ModularPatientDataContext);
  const { claims } = useAuth();
  const { usesNextStepsPad, isAgreedMedicationPlanEnabled, isPadTreatmentFirst = false, isAppointmentDatesEnabled = false } = useWorkflowConfig(patientDataModel, claims);
  const [hasChanges, setHasChanges] = useState(false);

  // Determines whether this user is the clinician assigned to the PAD stage.
  // When false the entire page renders in read-only mode — interactive elements
  // are disabled and section-completion / AI-generation calls are skipped.
  const canEdit = useCanEditStage(patientDataModel, 'PAD', { previewMode: isPreviewMode });

  // Registers dirty state with ProformaNavigationGuardContext and provides
  // unmount-based regeneration as a safety net for unmanaged exits (breadcrumb,
  // top nav, browser back). markRegenHandled() is called in handleSectionCompletion
  // to prevent double-firing when the managed Next/Finish/Sidebar path is used.
  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: 'CYP ADHD Post-Assessment Discussion',
    sectionTitle: 'Review of Assessment',
    hasChanges,
    canEdit,
    patientDataModel,
    isGenerationEnabled: CYP_PAD_LLM_PIPELINE_ENABLED,
  });

  // Add form state management
  const [formState, setFormState] = useState({});

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Add these handler functions near your other handler functions
  const handleFinish = async () => {
    setIsSaving(true);
    try {
      await handleSectionCompletion(patientId, currentStageTitle, currentSectionKey, hasChanges);
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Error completing section:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateReport = () => {
    setIsDialogOpen(false);
    if (!CYP_PAD_LLM_PIPELINE_ENABLED) {
      // No pipeline to wait on: complete the module and go to documents.
      handleNextPageButtonClick();
      return;
    }
    setIsGenerating(true);
  };

  const handleCancelGeneration = () => {
    setIsGenerating(false);
  };

  const handleNextPageButtonClick = async () => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['5_postAssessmentDiscussion']);

    await updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': buildPadCompletionFields(patientDataModel, firstCompletedFields),
      '6_manageReports': {
        padReportGenerated: true,
        padReportGeneratedAt: serverTimestamp(),
        padReportGeneratedBy: auth.currentUser.uid,
        padReportGeneratedByName: auth.currentUser.displayName,
        ...buildFirstGeneratedFields('pad', manageReports),
      }
    });

    appendReportAuditEntry(patientId, {
      module: 'pad',
      eventType: 'generated',
      isFirstOccurrence: !manageReports?.padReportFirstGeneratedAt,
    });

    // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'pad',
      didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
      enabled: isAppointmentDatesEnabled,
    });

    navigate(`/patient/${patientId}/cyp-adhd/documents`);
  };

  // Speech recognition states
  // const [activeRecording, setActiveRecording] = useState(null);
  // const [recognition, setRecognition] = useState(null);

  // // Speech recognition handlers
  // const handleRecording = (subsectionId) => {
  //   if (!('webkitSpeechRecognition' in window)) {
  //     alert('Speech recognition not supported in this browser.');
  //     return;
  //   }

  //   if (recognition) {
  //     recognition.stop();
  //     return;
  //   }

  //   const newRecognition = new window.webkitSpeechRecognition();
  //   newRecognition.continuous = true;
  //   newRecognition.interimResults = true;
  //   newRecognition.lang = 'en-GB';

  //   newRecognition.onresult = (event) => {
  //     let finalTranscript = notes[subsectionId] || '';
  //     for (let i = event.resultIndex; i < event.results.length; i++) {
  //       let transcript = event.results[i][0].transcript.trim();
  //       if (event.results[i].isFinal) {
  //         transcript = ' ' + transcript.charAt(0).toUpperCase() + transcript.slice(1) + '.';
  //         finalTranscript += transcript;
  //       }
  //     }
  //     handleNotesChange(subsectionId, finalTranscript);
  //   };

  //   newRecognition.onend = () => {
  //     setActiveRecording(null);
  //     setRecognition(null);

  //     // Simulate focus and blur to trigger onBlur
  //     const currentTextArea = textAreaRefs.current[subsectionId];
  //     if (currentTextArea) {
  //       currentTextArea.focus();
  //       setTimeout(() => {
  //         currentTextArea.blur();
  //       }, 0);
  //     }
  //   };

  //   newRecognition.start();
  //   setRecognition(newRecognition);
  //   setActiveRecording(subsectionId);

  //   // Focus the text area when recording starts
  //   const currentTextArea = textAreaRefs.current[subsectionId];
  //   if (currentTextArea) {
  //     currentTextArea.focus();
  //   }
  // };

  // Panel states 
  const [notes, setNotes] = useState({});

  // Tracks whether the goals textbox was prefilled from the MDT Plan notes
  // (shown as a dismissible banner, fades on first edit — RSK.js pattern).
  const [showMdtPlanPrefillBanner, setShowMdtPlanPrefillBanner] = useState(false);
  // True when this session copied MDT Plan notes into the PAD goals field
  // for the first time. Generation reads PAD clinicianNotes, not the MDT
  // Plan path, so we must persist that copy and treat it as a change even
  // if the clinician never focuses the box (no blur → no write).
  const mdtGoalsHydratedThisSession = useRef(false);
  const notesRef = useRef({});

  
  // isTOC gates the simplified two-page PAD ("Assessment Outcome" ->
  // "Next Steps") vs the standard multi-section DIS/TRT flow. Driven by
  // padFlow.structure rather than assessmentWorkflowType directly, so a
  // tocAdhd org can opt into the full PAD (e.g. LMP) via an explicit stamp.
  const isTOC = usesNextStepsPad;

  // Constants — display titles adapt based on whether this is a TOC patient.
  // The internal section key stays 'Review of Assessment' for backend compatibility
  // (LLM generation triggers and Firestore stageSectionsGenerated checks).
  const currentStageTitle = 'CYP ADHD Post-Assessment Discussion';
  const currentSectionNumber = 1;
  const currentSectionKey = 'Review of Assessment';
  const currentSectionTitle = isTOC ? 'Assessment Outcome' : 'Review of Assessment';
  const prevSectionLink = '/cyp-adhd';
  const prevSectionTitle = 'Patient Overview';

  // Ordered stage list for the clinician PAD — controls sidebar order, display
  // numbers, and prev/next links. Treatment-first (SLaM) swaps sections 2/3.
  const padStageOrder = useMemo(
    () => getPadStageOrder({ isPadTreatmentFirst, isAgreedMedicationPlanEnabled }),
    [isPadTreatmentFirst, isAgreedMedicationPlanEnabled]
  );

  // Navigation links derived from the ordered stage list. For Review (key '1'),
  // prev is always null (Patient Overview is handled separately), and next
  // points to whichever stage follows Review in the current order.
  const padNav = useMemo(
    () => getPadNavLinks('1', padStageOrder),
    [padStageOrder]
  );

  // TOC has its own next-steps page after Review; standard CYP (WLC) proceeds
  // through Strategies, Treatment Options, Health Checks when the diagnosis is
  // positive, or finishes here for negative outcomes. The padNav links resolve
  // the correct next stage from the ordered stage list.
  const nextSectionLink = isTOC
    ? '/proforma/cyp-adhd/post-assessment-discussion/next-steps'
    : padNav.nextLink;
  const nextSectionTitle = isTOC ? 'Next Steps' : padNav.nextTitle;

  // Get the section data using useMemo
  const sectionData = useMemo(() => {
    return patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[currentSectionNumber] || {};
  }, [patientDataModel]);

  // Get subsections data
  const subsections = useMemo(() => {
    return sectionData?.subsections || {};
  }, [sectionData]);

 const [openDialogIndex, setOpenDialogIndex] = useState(null);
 const [rawPrefillData, setRawPrefillData] = useState([]);
 const [dialogPrefillText, setDialogPrefillText] = useState('');

  // Resolve assessment goals for the read-only prefill row. On SLaM Path 1 the
  // clinician may reach PAD before opening Presenting Complaint, so we cannot
  // rely on the CI subsectionQuestions node existing — fall back to formData.
  const assessmentGoalsDisplay = useMemo(
    () => resolveAssessmentGoalsDisplay({
      dataModel: patientDataModel?.dataModel,
      formData: patientDataModel?.formData,
    }),
    [patientDataModel?.dataModel, patientDataModel?.formData],
  );

 //   // Update this function to fetch raw prefill data when dialog opens
  const handleOpenRawPrefillDialog = () => {
    const question = findCiAssessmentGoalsQuestion(patientDataModel?.dataModel);
    const varNames = question?.prefilledByServiceUserVarNames?.length
      ? question.prefilledByServiceUserVarNames
      : [ASSESSMENT_GOALS_SERVICE_USER_VAR];

    if (varNames.length > 0 && question && !question.clinicianInput) {
      const rawPrefillData = getRawPrefillInputs(varNames);
      setRawPrefillData(rawPrefillData);
      setDialogPrefillText(question.patientPrefill || assessmentGoalsDisplay.displayText || '');
      setOpenDialogIndex(0);
      return;
    }

    if (question) {
      setRawPrefillData([{
        formName: question.clinicianInput ? "Clinician Input" : "Your Current Concerns",
        question: question.title,
        answer: question.clinicianInput || question.patientPrefill || assessmentGoalsDisplay.displayText || "No previous answer given",
        hasAnswer: !!(question.clinicianInput || question.patientPrefill || assessmentGoalsDisplay.hasPatientSelfReport),
        lastUpdated: question.clinicianInputUpdatedAt || question.patientPrefillLastUpdatedAt,
        updatedBy: question.clinicianInput ? (question.clinicianInputUpdatedByName || "Unknown clinician") : null
      }]);
      setDialogPrefillText(question.patientPrefill || assessmentGoalsDisplay.displayText || '');
      setOpenDialogIndex(0);
      return;
    }

    const rawPrefillData = getRawPrefillInputs(varNames);
    if (rawPrefillData.length > 0) {
      setRawPrefillData(rawPrefillData);
      setDialogPrefillText(assessmentGoalsDisplay.displayText || '');
      setOpenDialogIndex(0);
    }
  };

   // Function to extract raw prefill inputs from formData
  const getRawPrefillInputs = (varNames) => {
    if (!patientDataModel?.formData?.yourCurrentConcerns?.formSubsections?.[1]?.questions ||
      !varNames || varNames.length === 0) {
      return [];
    }

    const results = [];
    const aboutYourReferralQuestions = patientDataModel.formData.yourCurrentConcerns.formSubsections[1].questions;
    
    // Go through all questions in "Your Current Concerns" form
    Object.entries(aboutYourReferralQuestions).forEach(([questionKey, question]) => {
      // Check each subquestion
      if (question.subquestions) {
        Object.entries(question.subquestions).forEach(([subquestionKey, subquestion]) => {
          // Skip non-numbered subquestion keys
          if (isNaN(parseInt(subquestionKey))) return;

          // If this subquestion's variable name is in our list
          if (varNames.includes(subquestion.serviceUserVarName)) {
            const normalized = normalizeRawAnswer(subquestion.answer, subquestion.otherAnswer);
            const allOptions = [...(subquestion.options || []), ...(subquestion.noneOptions || [])];

            results.push({
              formName: "Your Current Concerns",
              questionTitle: question.questionTitle,
              question: subquestion.subquestionDisplayText,
              answer: buildDisplayAnswer(normalized),
              hasAnswer: normalized !== null && normalized !== undefined && normalized !== '',
              lastUpdated: question.lastUpdated,
              updatedBy: null,
              inputType: subquestion.inputType,
              options: allOptions,
              rawAnswer: normalized,
              otherOptions: subquestion.otherOptions || {},
              wasSkipped: question.wasSkipped,
              skipButtonText: question.skipButtonText
            });
          }
        });
      }
    });

    return results;
  };

  // Add this function to check if an option is selected
  const isOptionSelected = (option, rawAnswer) => {
    if (!rawAnswer) return false;
    
    // For simple string match
    if (typeof rawAnswer === 'string') {
      return rawAnswer === option;
    }
    
    // For array of strings
    if (Array.isArray(rawAnswer) && typeof rawAnswer[0] === 'string') {
      return rawAnswer.includes(option);
    }
    
    // For array of objects with value property
    if (Array.isArray(rawAnswer) && typeof rawAnswer[0] === 'object') {
      return rawAnswer.some(ans => ans.value === option);
    }
    
    // For single object with value property
    if (rawAnswer && typeof rawAnswer === 'object' && 'value' in rawAnswer) {
      return rawAnswer.value === option;
    }
    
    return false;
  };

  // Add a ref to track initial load
  const isInitialLoad = useRef(true);

  useEffect(() => {
    // Only run on initial load
    if (!isInitialLoad.current) return;
    if (!sectionData?.subsections) return;

    const initialFormState = {};
    const initialNotes = {};

    Object.entries(sectionData.subsections).forEach(([subsectionId, subsection]) => {
      // Initialize notes if they exist
      if (subsection?.clinicianNotes) {
        // Normalise: notes written before this box was structured are a plain
        // string, and become a single note rather than being dropped.
        initialNotes[subsectionId] = normaliseNotesValue(subsection.clinicianNotes);
      }

      // Initialize form state for questions
      if (subsection?.questions) {
        Object.entries(subsection.questions).forEach(([questionId, question]) => {
          if (question?.firestoreFieldName) {
            initialFormState[question.firestoreFieldName] = {
              value: question?.answer?.value || []
            };
          }
        });
      }
    });

    // MDT Plan goals: the box is shown from MDT notes when PAD clinicianNotes
    // has never been written. Showing the text in React state is not enough —
    // generation reads the PAD field, and blur only fires if the box is
    // focused. Persist the copy immediately so an untouched prefill still
    // reaches the goals paragraph. An explicit empty string (clinician
    // cleared the box) is a written value and must not be overwritten.
    const storedPadGoalsNotes = sectionData.subsections?.['2']?.clinicianNotes;
    const mdtPlanGoalsNotes = patientDataModel?.dataModel?.['4_diagnosticDecision']?.mdtPlan?.patientGoalsNotes || '';
    const resolvedGoals = resolveCypPadGoalsNotes({
      padNotes: storedPadGoalsNotes,
      mdtPlanNotes: mdtPlanGoalsNotes,
    });
    if (resolvedGoals.source === 'mdtPlan') {
      initialNotes['2'] = resolvedGoals.value;
      setShowMdtPlanPrefillBanner(true);
      mdtGoalsHydratedThisSession.current = true;
      if (canEdit) {
        updatePatientFirestore(patientId, {
          '5_postAssessmentDiscussion': {
            sections: {
              [currentSectionNumber]: {
                subsections: {
                  '2': { clinicianNotes: resolvedGoals.value },
                },
              },
            },
          },
        });
        // Treat the copy as a real input change so section completion and
        // unmanaged exits regenerate the goals paragraph (including patients
        // whose first generation ran against an empty PAD field).
        setHasChanges(true);
      }
    }

    setFormState(initialFormState);
    setNotes(initialNotes);
    notesRef.current = initialNotes;

    // Mark initial load as complete
    isInitialLoad.current = false;
  }, [sectionData, patientDataModel, canEdit, patientId, updatePatientFirestore]); 

    // Check if any required notes are empty
    const hasEmptyRequiredNotes = useMemo(() => {
      return Object.entries(subsections).some(([subsectionId, subsection]) => {
        // Skip if subsection is null, undefined, or shouldn't be displayed
        if (!subsection || !ShouldDisplayElement(subsection, patientDataModel)) {
          return false;
        }
  
        // Check if notes are required and empty
        return subsection.notesRequired === true && !hasNoteText(notes[subsectionId]);
      });
    }, [subsections, notes, patientDataModel]);
  


  const handleMultipleSelect = async (subsectionId, fieldName, option, isSelected) => {
    if (!canEdit) return;
    try {
      // Get current selections
      const currentSelections = formState[fieldName]?.value || [];
      let newSelections;

      if (isSelected) {
        // Remove the option if it's already selected
        newSelections = currentSelections.filter(opt => opt !== option);
      } else {
        // Add the new option
        newSelections = [...currentSelections, option];
      }

      // Create update object
      const updateObject = {
        value: newSelections
      };

      // Update local state first
      setFormState(prev => ({
        ...prev,
        [fieldName]: updateObject
      }));

      // Find the question index in the subsection
      const questionIndex = Object.entries(sectionData.subsections[subsectionId].questions)
        .find(([_, q]) => q.firestoreFieldName === fieldName)?.[0];

      if (!questionIndex) {
        throw new Error('Question not found');
      }

      // Uses nested-object format (matching CI/SSI convention) so the write
      // can be deep-merged with other section writes when batched together.
      await updatePatientFirestore(patientId, {
        '5_postAssessmentDiscussion': {
          sections: {
            [currentSectionNumber]: {
              subsections: {
                [subsectionId]: {
                  questions: {
                    [questionIndex]: { answer: updateObject }
                  }
                }
              }
            }
          }
        }
      });
      setHasChanges(true);
    } catch (error) {
      console.error('Error updating Firestore:', error);
      // Revert local state if Firestore update fails
      setFormState(prev => ({
        ...prev,
        [fieldName]: {
          value: formState[fieldName]?.value || []
        }
      }));
    }
  };

  // Add the notes handlers
  const handleNotesChange = (subsectionId, value) => {
    if (!canEdit) return;
    setNotes(prev => {
      const next = { ...prev, [subsectionId]: value };
      notesRef.current = next;
      return next;
    });
    setHasChanges(true);
    // Dismiss the MDT Plan prefill banner once the clinician edits the goals.
    if (subsectionId === '2' && showMdtPlanPrefillBanner) {
      setShowMdtPlanPrefillBanner(false);
    }
  };

  // Persists a subsection's notes to Firestore.
  //
  // IMPORTANT: this does NOT call setHasChanges(true). Dirty-tracking lives
  // exclusively in handleNotesChange; keeping the two apart is what stopped a
  // save-without-edit from flagging the page dirty and tripping the
  // navigation guard.
  const persistSubsectionNotes = useCallback((subsectionId, value) => {
    // Patient's Goals: persist '' when cleared so generation can tell
    // "clinician deleted the MDT prefill" from "PAD notes were never written"
    // (null). Other subsections keep the historical null-for-empty write.
    // Patient's Goals: persist [] when cleared so generation can tell
    // "clinician deleted the MDT prefill" from "PAD notes were never written"
    // (null). Other subsections keep the historical null-for-empty write.
    const notesArray = normaliseNotesValue(value);
    const clinicianNotes = subsectionId === '2'
      ? notesArray
      : (notesArray.length ? notesArray : null);
    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        sections: {
          [currentSectionNumber]: {
            subsections: {
              [subsectionId]: { clinicianNotes },
            },
          },
        },
      },
    });
  }, [patientId, updatePatientFirestore]);


  const [isDiagnosisADHD, setIsDiagnosisADHD] = useState(false);

  useEffect(() => {
    setIsDiagnosisADHD(isPadAdhdOutcomePositive(patientDataModel));
  }, [patientDataModel]);

  const SECTIONS_PAD = useMemo(() => {
    // TOC uses a simplified sidebar: "Assessment Outcome" (always) + "Next Steps"
    // (positive diagnosis only, locked until Patient Goals is filled in).
    // When the diagnosis is negative the PAD ends at Assessment Outcome, so
    // the "Next Steps" entry is omitted entirely.
    if (isTOC) {
      const sections = [
        { sectionTitle: 'Assessment Outcome', route: '/proforma/cyp-adhd/post-assessment-discussion/review-assessment', status: 'in-progress', isUnlocked: true },
      ];
      if (isDiagnosisADHD) {
        // "Next Steps" is locked until Patient Goals (subsection 2 clinicianNotes) has a non-null value
        const patientGoalsNote = subsections?.['2']?.clinicianNotes;
        const isNextStepsUnlocked = hasNoteText(patientGoalsNote);
        sections.push({
          sectionTitle: 'Next Steps',
          route: '/proforma/cyp-adhd/post-assessment-discussion/next-steps',
          status: 'pending',
          isUnlocked: isNextStepsUnlocked,
        });
      }
      return sections;
    }

    // Standard (non-TOC) multi-section sidebar — uses the shared helper to
    // produce a correctly-ordered and unlock-gated sidebar for both
    // strategies-first and treatment-first workflows.
    const isCarryOutHealthChecksNow = readIsCarryOutHealthChecksNow(patientDataModel);

    const isBeginningMedicationNow = isCarryOutHealthChecksNow && patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.['4']?.subsections?.['4']?.questions?.['2']?.answer?.value?.includes("Yes");

    const section1Complete = !hasEmptyRequiredNotes;

    return getPadSidebarSections(
      patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections,
      padStageOrder,
      buildPadSidebarUnlockFlags({
        patientDataModel,
        isPadTreatmentFirst,
        isDiagnosisADHD,
        isCarryOutHealthChecksNow,
        isBeginningMedicationNow,
        section1Complete,
      }),
    );
  }, [patientDataModel, isDiagnosisADHD, hasEmptyRequiredNotes, padStageOrder, isPadTreatmentFirst]);

  const sendSectionUpdates = useCallback((newUpdates) => {
    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        sections: {
          [currentSectionNumber]: newUpdates
        }
      }
    });
  }, [patientId, currentSectionNumber, updatePatientFirestore]);


  const handleSectionCompletion = useCallback((patientId, currentStageTitle, sectionKey, hasChanges) => {
    if (!canEdit) return Promise.resolve();

    markRegenHandled();

    const currentSectionData = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[currentSectionNumber];
    const isAlreadyCompleted = currentSectionData?.status === 'completed';
    const sectionsAlreadyGenerated = patientDataModel?.dataModel?.['6_manageReports']?.sections?.['4']?.stageSectionsGenerated;
    const isCurrentSectionGenerated = sectionsAlreadyGenerated?.includes(sectionKey) || false;

    // Flush any local notes that never made it to Firestore (untouched MDT
    // prefill, or typed text whose blur did not fire). Generation reads the
    // PAD field, so this write must land before flushPendingUpdates resolves.
    const liveNotes = notesRef.current || {};
    Object.entries(liveNotes).forEach(([subsectionId, value]) => {
      const storedValue = currentSectionData?.subsections?.[subsectionId]?.clinicianNotes;
      if (!sameNotes(value, storedValue)) {
        persistSubsectionNotes(subsectionId, value);
      }
    });

    // Determine what work actually needs doing on this exit
    const needsStatusWrite = !isAlreadyCompleted;
    const needsGeneration = hasChanges
      || !isCurrentSectionGenerated
      || mdtGoalsHydratedThisSession.current;

    // No-op fast path: section already complete, already generated, no edits
    if (!needsStatusWrite && !needsGeneration) return Promise.resolve();

    if (needsStatusWrite) {
      sendSectionUpdates({ status: 'completed', progress: 100 });
    }

    const dataReady = flushPendingUpdates({ noTimeout: true });

    if (needsGeneration) {
      // Use sectionKey (the stable Firestore key) not currentSectionTitle, since for TOC
      // the display title ('Assessment Outcome') differs from the storage key ('Review of Assessment').
      if (CYP_PAD_LLM_PIPELINE_ENABLED) {
        TriggerGenerateDocumentSection(patientId, currentStageTitle, sectionKey, [], dataReady);
      }
    }

    return dataReady;
  }, [canEdit, patientDataModel, currentSectionNumber, sendSectionUpdates, flushPendingUpdates, markRegenHandled, persistSubsectionNotes]);

  const [isNavigating, setIsNavigating] = useState(false);

  const handleNextSection = useCallback(() => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion(patientId, currentStageTitle, currentSectionKey, hasChanges).then(() => {
      navigate(`/patient/${patientId}${nextSectionLink}`);
    });
  }, [isNavigating, patientId, navigate, nextSectionLink, hasChanges, handleSectionCompletion]);

  const navigateToSection = useCallback((index) => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion(patientId, currentStageTitle, currentSectionKey, hasChanges).then(() => {
      const section = SECTIONS_PAD[index];
      if (section.route) {
        navigate(`/patient/${patientId}${section.route}`);
      }
    });
  }, [isNavigating, patientId, navigate, SECTIONS_PAD, hasChanges, handleSectionCompletion]);

  const handleBackNavigation = useCallback(() => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion(patientId, currentStageTitle, currentSectionKey, hasChanges).then(() => {
      navigate(`/patient/${patientId}${prevSectionLink}`);
    });
  }, [isNavigating, patientId, navigate, prevSectionLink, hasChanges, handleSectionCompletion]);

  const breadcrumbTuples = [
    ["Patient Overview", `/patient/${patientId}/cyp-adhd`],
    [currentStageTitle],
    [currentSectionTitle]
  ];

  // After a SLaM PAD redo the live proforma looks brand new. Surface a quiet
  // reminder + archive link so clinicians can still open the superseded
  // discussion (the overview / documents "View original" used to vanish once
  // staleness cleared).
  const padRedoRecord =
    patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.redo || null;
  const padRedoArchiveId = padRedoRecord?.lastRedoArchiveId || null;

  // Counter text for the footer -- shows "1 of N" where N is the total
  // number of PAD stages. TOC always has exactly 2 pages.
  const counterText = isTOC
    ? '1 of 2'
    : `${getPadSidebarIndex(padStageOrder, '1') + 1} of ${padStageOrder.length}`;

  // Whether the forward action is a "Go to next stage" button (positive
  // diagnosis with a next stage available) vs a "Finish" button (negative
  // diagnosis or this is the last reachable stage).
  const showNextStage = !!(isDiagnosisADHD && nextSectionLink);

  // Finish button disabled when the diagnosis is positive but goals notes
  // are still empty -- the clinician must fill them in first.
  const isFinishDisabled =
    (canEdit && isDiagnosisADHD && hasEmptyRequiredNotes) || isSaving || isNavigating;

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <div className="flex flex-col h-full min-h-0">
        <div className="flex-shrink-0">
          <Breadcrumb tupleList={breadcrumbTuples} />
        </div>
        <div className="flex flex-1 min-h-0">
          <PADSideNavbar
            sections={SECTIONS_PAD}
            currentSectionIndex={isTOC ? 0 : getPadSidebarIndex(padStageOrder, '1')}
            navigateToSection={navigateToSection}
            isNavigating={isSaving || isNavigating}
          />
          <div className="flex-1 flex flex-col min-h-0">
            {isPreviewMode && <PreviewModeBanner />}
            <div className="flex-1 overflow-y-auto p-8">
              {padRedoArchiveId && (
                <div className="mb-6">
                  <PadRedoArchiveBanner
                    isVisible
                    snapshotPath={`/patient/${patientId}/pad-snapshot/${padRedoArchiveId}`}
                    redoFromOutcome={padRedoRecord?.redoFromOutcome || null}
                  />
                </div>
              )}

              {/* Section Title -- contract-type badge right-aligned opposite the
                  heading, matching the Next Steps page. Gated on the clinician's
                  org, so it only shows for orgs that categorise by contract type. */}
              <div className="flex justify-between items-center gap-3 mb-6">
                <h2 className="text-2xl font-bold">{padNav.displayNumber}. {currentSectionTitle}</h2>
                <PatientContractTypeBadge
                  label="Contract"
                  organisation={claims?.organisation}
                  assessmentType={patientDataModel?.customFields?.assessmentType}
                  subContractType={patientDataModel?.customFields?.subContractType}
                />
              </div>

          {/* Supporting Documents: the approved CYP Clinical Interview and
              Semi-Structured Interview PDFs, for reference during the
              discussion (parity with the adult PAD and MDT Review).
              Hidden when neither is approved yet. */}
          <CypAdhdSupportingDocumentsPanel
            patientId={patientId}
            clinicianId={auth.currentUser?.uid || ''}
            documents={modularHeader?.documents}
            reportContent={modularReportContent}
          />

          {/* Subsections */}
          {Object.entries(subsections).map(([subsectionId, subsection]) => {
            // Skip if subsection is null or undefined
            if (!subsection) return null;

            if (!ShouldDisplayElement(subsection, patientDataModel)) {
              return null;
            }

            return (
              <div key={subsectionId} className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200">
                <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                  {rewriteSubsectionTitleDisplay(subsection.subsectionTitleDisplay, padNav.displayNumber)}                
                </h3>

                {/* Assessment goals read-only row — always shown on Patient's Goals
                    so Path 1 (CI Review → PAD) still surfaces intake answers even
                    when Presenting Complaint was never opened. */}
                {subsectionId === '2' && (
                  <div className="mb-6 mt-2 rounded-lg">
                    <div className="text-slate-700 text-base flex text-left items-center mb-2">
                      <div className="w-96 flex items-center gap-2">
                        <span className="font-bold">Assessment goals</span>
                        {assessmentGoalsDisplay.hasPatientSelfReport ? (
                          <p className="text-sm italic text-gray-600">(from Parent/carer report)</p>
                        ) : (
                          <p className="text-sm italic text-gray-400">(No answer given by the parent/carer)</p>
                        )}
                      </div>
                    
                      <div className="flex items-center">
                        <div className="w-96 p-3 border rounded-md bg-gray-100 text-gray-700 cursor-not-allowed whitespace-pre-wrap">
                          <FormattedAnswer
                            rawAnswer={assessmentGoalsDisplay.rawAnswer}
                            displayText={assessmentGoalsDisplay.displayText || "No answer given"}
                          />
                        </div>
                        
                        {/* Source button — only when there is an intake answer to inspect */}
                        {assessmentGoalsDisplay.hasPatientSelfReport && (
                        <Dialog.Root open={openDialogIndex === 0} onOpenChange={(open) => !open && setOpenDialogIndex(null)}>
                          <Dialog.Trigger asChild>
                            <div
                              className="cursor-pointer ml-1 mt-2"
                              onClick={handleOpenRawPrefillDialog}
                            >
                              <div className="flex items-center gap-1 text-xs text-violet-800 font-medium rounded-full px-1 py-1 transition-all duration-200 hover:bg-violet-100">
                                <SearchIcon className="h-3 w-3" />
                              </div>
                            </div>
                          </Dialog.Trigger>
                          
                          <Dialog.Portal>
                            <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
                            <Dialog.Content className="radix-dialog-content-centered-motion fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg w-[700px] max-h-[65vh] overflow-y-auto">
                              <div className="flex justify-between items-center mb-4">
                                <Dialog.Title className="text-base font-semibold text-gray-900">
                                  Patient Form Responses
                                </Dialog.Title>
                                <Dialog.Close asChild>
                                  <button className="p-1 rounded-full hover:bg-gray-100">
                                    <XIcon className="h-5 w-5 text-gray-500" />
                                  </button>
                                </Dialog.Close>
                              </div>

                              <div className="mb-4">
                                <h3 className="font-semibold text-sm mb-2">Patient's Goals</h3>

                                {/* Last updated timestamp */}
                                {rawPrefillData[0]?.lastUpdated && (
                                  <div className="text-xs text-gray-500 mb-3 flex items-center">
                                    <ClockIcon className="h-3 w-3 mr-1" />
                                    Last updated: {formatDateTime(rawPrefillData[0].lastUpdated)}
                                  </div>
                                )}

                                {/* Display the dialog content */}
                                <div className="space-y-2 mt-2">
                                  {rawPrefillData.length > 0 ? (
                                    rawPrefillData.map((item, i) => (
                                      <div key={i} className={`border rounded-md p-3 ${item.hasAnswer
                                          ? 'bg-violet-50 shadow-sm border-violet-200'
                                          : 'bg-gray-50 border-dashed border-gray-200 opacity-75'
                                        }`}>
                                        <div className="text-xs text-gray-500 mb-0.5">From: {item.formName}</div>
                                        
                                        {/* Display question title if available */}
                                        {item.questionTitle && (
                                          <div className="text-xs font-medium text-gray-700 mb-1">{item.questionTitle}</div>
                                        )}
                                        
                                        <div className="text-xs font-medium mb-1.5">{item.question}</div>

                                        {/* Display skip badge if question was skipped */}
                                        {item.wasSkipped && (
                                          <div className="text-xs text-gray-500 flex justify-start items-center mb-2">
                                            <span>Patient clicked</span>
                                            <span className="text-xs bg-violet-50 shadow-sm border-violet-200 border text-gray-800 font-semibold px-2 py-1 rounded-md ml-2 whitespace-nowrap">
                                              {item.skipButtonText}
                                            </span>
                                          </div>
                                        )}

                                        {/* Display the answer — FormattedAnswer renders any otherValue
                                            free text inline in italic curly quotes. */}
                                        <div className={`p-2 rounded border whitespace-pre-wrap text-xs ${item.hasAnswer
                                            ? 'bg-white border-gray-200'
                                            : 'bg-gray-100 border-gray-200 border-dashed text-gray-500 italic'
                                          }`}>
                                          <FormattedAnswer
                                            rawAnswer={item.rawAnswer}
                                            displayText={rawPrefillData.length === 1 && dialogPrefillText ? dialogPrefillText : item.answer}
                                          />
                                        </div>
                                        
                                        {/* Display available options for selection questions */}
                                        {item.options && item.options.length > 0 && (
                                          <div className="mt-2">
                                            <div className="text-xs text-gray-500 mb-1">Available options:</div>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                                              {item.options.map((option, optIndex) => {
                                                const isSelected = item.hasAnswer && isOptionSelected(option, item.rawAnswer);
                                                
                                                return (
                                                  <div
                                                    key={optIndex}
                                                    className={`text-xs p-1 rounded ${
                                                      isSelected
                                                        ? 'bg-violet-100 border border-violet-300 text-violet-700 font-medium'
                                                        : 'bg-gray-50 border border-gray-200 text-gray-500'
                                                    }`}
                                                  >
                                                    {option}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}

                                      </div>
                                    ))
                                  ) : (
                                    <p className="text-gray-500 italic">No source data available</p>
                                  )}
                                </div>
                              </div>
                            </Dialog.Content>
                          </Dialog.Portal>
                        </Dialog.Root>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <p className="text-left text-gray-700">
                {subsection.tooltipText && (
                   subsection.tooltipText                      
                  )}
                </p>
               

                <div className="space-y-6">
                  {Object.entries(subsection.questions || {}).map(([questionId, question]) => {
                    // Skip if question is null or undefined
                    if (!question) return null;

                    // Check if question should be displayed based on conditions
                    if (!ShouldDisplayElement(question, patientDataModel)) {
                      return null;
                    }

                    return (
                      <div key={questionId} className="flex items-start">
                        <div className="w-96 flex items-center gap-2">
                          {question.tooltipText ? (
                            <InfoTooltip
                              content={question.tooltipText}
                              label={`Information about ${question.questionTitle}`}
                              maxWidth="300px"
                              className=""
                            />
                          ) : (
                            <div className="w-4 h-4"></div>
                          )}
                          <h4 className="text-left font-semibold text-gray-700">
                            {question.questionTitle}
                          </h4>
                        </div>

                        <div className="ml-5">
                          <ul className="items-center flex flex-row gap-4">
                            {(question.options || []).map((option) => {
                              const currentSelections = formState[question.firestoreFieldName]?.value || [];
                              const isSelected = currentSelections.includes(option);

                              return (
                                <li
                                  key={option}
                                  onClick={() => canEdit && handleMultipleSelect(
                                    subsectionId,
                                    question.firestoreFieldName,
                                    option,
                                    isSelected
                                  )}
                                  className={`
                                    flex-1 p-2 w-64
                                    rounded-lg transition-all duration-300 ease-in-out
                                    text-center
                                    flex flex-row items-center justify-center gap-2
                                    ${!canEdit
                                      ? 'cursor-not-allowed opacity-75'
                                      : 'cursor-pointer group'
                                    }
                                    ${isSelected
                                      ? 'bg-violet-600 border-2 border-violet-800 font-semibold shadow-lg transform scale-105'
                                      : canEdit
                                        ? 'bg-white hover:bg-gray-50 border-2 border-dashed border-gray-300 hover:border-gray-400 shadow-sm hover:shadow-md'
                                        : 'bg-white border-2 border-dashed border-gray-300 shadow-sm'
                                    }
                                  `}
                                >
                                  <div className={`transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`}>
                                    {isSelected ? (
                                      <CheckIcon className="w-4 h-4" />
                                    ) : (
                                      <PlusIcon className="w-4 h-4" />
                                    )}
                                  </div>
                                  <span className={`font-semibold transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>
                                    {option}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    );

                  })}
                </div>

                {/* Clinician Notes Box */}
                {subsection.hasClinicianNotesBox && (
                  <div className="text-left mt-6">
                    <h4 className="text-gray-700 text-sm font-semibold mb-2">
                      {subsection.textBoxSubtitle}
                      {subsection.notesRequired &&
                        <span className="text-red-600 ml-1">*</span>
                      }
                    </h4>

                    {/* MDT Plan prefill banner — shown when goals text was
                        prefilled from the MDT Plan; disappears on first edit */}
                    {subsectionId === '2' && (
                      <div className={`overflow-hidden transition-all duration-400 ease-in-out ${showMdtPlanPrefillBanner ? 'max-h-20 mb-3 opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 flex items-center gap-2">
                          <CheckCircleIcon className="w-4 h-4 text-violet-600 flex-shrink-0" />
                          <p className="text-sm text-violet-800">
                            This text has been prefilled from the MDT Plan. Edit as needed for the patient letter.
                          </p>
                        </div>
                      </div>
                    )}

                    <StructuredNotesEditor
                      notes={notes[subsectionId]}
                      onChange={(next) => {
                        handleNotesChange(subsectionId, next);
                        // There is no blur to save on any more, so each
                        // committed note is written as it is made.
                        if (canEdit) persistSubsectionNotes(subsectionId, next);
                      }}
                      placeholder="Add a note, then press Enter to save each point"
                      readOnlyPlaceholder="Notes can only be added by the assigned clinician"
                      minHeight="8rem"
                      domainId={`padReview-${subsectionId}`}
                      readOnly={!canEdit}
                    />
                  </div>
                )}
              </div>
            )
          })}

            </div>

            {/* Pinned footer navigation -- matches the CYP CI / Adult Autism
                modular proforma pattern. Back always goes to Patient Overview;
                forward is either "Go to next stage" or "Finish" depending on
                the diagnosis outcome and available stages. */}
            <ModularStepFooter
              showBack
              backLabel={`Back to ${prevSectionTitle}`}
              onBack={handleBackNavigation}
              backDisabled={isSaving || isNavigating}
              counterText={counterText}
              onNext={showNextStage ? handleNextSection : undefined}
              nextLabel={showNextStage ? `Go to ${nextSectionTitle}` : undefined}
              nextDisabled={(canEdit && hasEmptyRequiredNotes) || isNavigating}
              showNext={showNextStage}
              rightActions={
                !showNextStage && canEdit ? (
                  <button
                    onClick={handleFinish}
                    disabled={isFinishDisabled}
                    title={canEdit && isDiagnosisADHD && hasEmptyRequiredNotes
                      ? 'Please provide details about the patient\'s goals before proceeding'
                      : undefined}
                    className={`flex items-center py-2 px-4 rounded-md shadow-sm transition-all duration-200
                      ${isFinishDisabled
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-primary text-white hover:bg-primary/90'}`}
                  >
                    <span className="font-semibold text-sm">
                      {isSaving ? 'Saving Progress...' : 'Finish Post-Assessment Discussion'}
                    </span>
                    {isSaving
                      ? <ButtonSpinner className="w-5 h-5 ml-2" />
                      : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                  </button>
                ) : undefined
              }
            />
          </div>
        </div>
      </div>

      <ModuleCompletionDialog
        open={isDialogOpen && !isGenerating}
        onOpenChange={setIsDialogOpen}
        title="Post-Assessment Discussion Complete"
        width={600}
        actions={
          <>
            <CancelButton onClick={() => setIsDialogOpen(false)} />
            <GenerateReportButton onClick={handleGenerateReport} />
          </>
        }
      >
        <SummarySection title="Summary:">
          The Post-Assessment Discussion has been completed. Would you like to generate the report?
        </SummarySection>
      </ModuleCompletionDialog>

      <GenerateReportProgressDialog
        reportStage="CYP ADHD Post-Assessment Discussion"
        patientId={patientId}
        clinicianId={auth.currentUser?.uid || ''}
        handleNextPageButtonClick={handleNextPageButtonClick}
        isGenerating={isGenerating}
        onCancel={handleCancelGeneration}
      />
    </Layout>
  );
}